import React, { useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import { signOut } from 'firebase/auth';
import {
  collection, query, where,
  onSnapshot, doc, updateDoc, deleteDoc, serverTimestamp,
} from 'firebase/firestore';
import { useAuth } from '../AuthContext';
import CurriculumEditor from './CurriculumEditor';
import TeacherCurriculumView from './TeacherCurriculumView';
import { BOARD_LABELS } from '../data/curriculumData';

// ── Teacher Dashboard ─────────────────────────────────────────────────────────

export default function TeacherDashboard() {
  const { currentUser, userProfile } = useAuth();
  const [view, setView] = useState('dashboard');
  const [activeTab, setActiveTab] = useState('students');

  // ── Data
  const [pendingRequests, setPendingRequests]   = useState([]);
  const [approvedStudents, setApprovedStudents] = useState([]);
  const [curricula, setCurricula]               = useState([]);
  const [tasks, setTasks]                       = useState([]);
  const [submissions, setSubmissions]           = useState([]);
  const [loadingData, setLoadingData]           = useState(true);
  const [actionLoading, setActionLoading]       = useState(null);

  useEffect(() => {
    if (!currentUser) return;

    const unsubPending = onSnapshot(
      query(collection(db, 'joinRequests'),
        where('teacherId', '==', currentUser.uid),
        where('status', '==', 'pending')),
      (snap) => {
        setPendingRequests(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoadingData(false);
      }
    );

    const unsubApproved = onSnapshot(
      query(collection(db, 'joinRequests'),
        where('teacherId', '==', currentUser.uid),
        where('status', '==', 'approved')),
      (snap) => setApprovedStudents(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );

    const unsubCurricula = onSnapshot(
      query(collection(db, 'teacherCurricula'),
        where('teacherId', '==', currentUser.uid)),
      (snap) => setCurricula(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );

    const unsubSubmissions = onSnapshot(
      query(collection(db, 'taskSubmissions'),
        where('teacherId', '==', currentUser.uid)),
      (snap) => setSubmissions(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );

    const unsubTasks = onSnapshot(
      query(collection(db, 'tasks'),
        where('teacherId', '==', currentUser.uid)),
      (snap) => setTasks(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );

    return () => {
      unsubPending(); unsubApproved(); unsubCurricula();
      unsubSubmissions(); unsubTasks();
    };
  }, [currentUser]);

  const handleApprove = async (requestId) => {
    setActionLoading(requestId + '-approve');
    try {
      await updateDoc(doc(db, 'joinRequests', requestId), {
        status: 'approved', updatedAt: serverTimestamp(),
      });
    } catch (err) { console.error(err); }
    setActionLoading(null);
  };

  const handleDeny = async (requestId) => {
    setActionLoading(requestId + '-deny');
    try {
      await updateDoc(doc(db, 'joinRequests', requestId), {
        status: 'denied', updatedAt: serverTimestamp(),
      });
    } catch (err) { console.error(err); }
    setActionLoading(null);
  };

  const handleDeleteCurriculum = async (id) => {
    if (!window.confirm('Delete this curriculum? This cannot be undone.')) return;
    try {
      await deleteDoc(doc(db, 'teacherCurricula', id));
    } catch (err) { console.error(err); }
  };

  const handleSignOut = () => signOut(auth);
  const firstName = userProfile?.name?.split(' ')[0] || 'Teacher';

  const studentStats = approvedStudents.map((s) => {
    const assigned = tasks.filter((t) => t.assignedStudentIds?.includes(s.studentId));
    const completed = submissions.filter((sub) => sub.studentId === s.studentId);
    const pct = assigned.length > 0
      ? Math.round((completed.length / assigned.length) * 100)
      : 0;
    return { ...s, assignedCount: assigned.length, completedCount: completed.length, pct };
  });

  const renderStudentsTab = () => (
    <div className="tab-content animate-in">
      {studentStats.length === 0 ? (
        <div className="empty-state">
          <span>📭</span>
          <p>No approved students yet. Approve join requests in the Onboarding tab.</p>
        </div>
      ) : (
        <div className="student-task-list">
          {studentStats.map((s, idx) => (
            <div key={s.id} className="student-task-card" style={{ animationDelay: `${idx * 0.04}s` }}>
              <div className="stc-left">
                <div className="student-avatar student-avatar--lg">
                  {s.studentName?.charAt(0)?.toUpperCase() || '?'}
                </div>
                <div className="stc-info">
                  <strong className="stc-name">{s.studentName}</strong>
                  <span className="stc-email">{s.studentEmail}</span>
                </div>
              </div>
              <div className="stc-right">
                <div className="stc-task-stats">
                  <span className="stc-stat">
                    <span className="stc-stat-num">{s.assignedCount}</span>
                    <span className="stc-stat-label">Assigned</span>
                  </span>
                  <span className="stc-divider">·</span>
                  <span className="stc-stat">
                    <span className="stc-stat-num stc-stat-num--done">{s.completedCount}</span>
                    <span className="stc-stat-label">Done</span>
                  </span>
                </div>
                {s.assignedCount > 0 && (
                  <div className="stc-progress-bar">
                    <div className="stc-progress-fill" style={{ width: `${s.pct}%` }} />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderOnboardingTab = () => (
    <div className="tab-content animate-in">
      <div className="section">
        <h3 className="section-title">
          Pending Requests
          {pendingRequests.length > 0 && <span className="badge">{pendingRequests.length}</span>}
        </h3>
        {pendingRequests.length === 0 ? (
          <div className="empty-state">
            <span>✅</span>
            <p>No pending requests. Your classroom is all caught up!</p>
          </div>
        ) : (
          <div className="request-list" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {pendingRequests.map((req) => (
              <div key={req.id} className="student-task-card">
                <div className="stc-left">
                  <div className="student-avatar" style={{ background: 'var(--accent)', color: '#fff' }}>
                    {req.studentName?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                  <div className="stc-info">
                    <strong className="stc-name">{req.studentName}</strong>
                    <span className="stc-email">{req.studentEmail}</span>
                  </div>
                </div>
                <div className="stc-right">
                  <div className="request-actions" style={{ display: 'flex', gap: '8px' }}>
                    <button
                      className="btn-approve"
                      onClick={() => handleApprove(req.id)}
                      disabled={!!actionLoading}
                    >
                      {actionLoading === req.id + '-approve' ? '…' : '✓ Approve'}
                    </button>
                    <button
                      className="btn-deny"
                      onClick={() => handleDeny(req.id)}
                      disabled={!!actionLoading}
                    >
                      {actionLoading === req.id + '-deny' ? '…' : '✗ Deny'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {approvedStudents.length > 0 && (
        <div className="section" style={{ marginTop: 32 }}>
          <h3 className="section-title">
            Recently Enrolled
            <span className="badge badge--success" style={{ background: 'var(--success)' }}>{approvedStudents.length}</span>
          </h3>
          <div className="onboarding-enrolled-list" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {approvedStudents.slice(0, 5).map((s) => (
              <div key={s.id} className="student-task-card" style={{ padding: '12px 20px', background: 'var(--bg)' }}>
                <div className="stc-left">
                  <div className="student-avatar" style={{ width: 32, height: 32, fontSize: '14px' }}>
                    {s.studentName?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                  <div className="stc-info">
                    <strong style={{ fontSize: '14px' }}>{s.studentName}</strong>
                    <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{s.studentEmail}</span>
                  </div>
                </div>
                <span className="chip chip--success chip--sm">Enrolled</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const renderCurriculaTab = () => (
    <div className="tab-content animate-in">
      <div className="section-header-row" style={{ marginBottom: 20 }}>
        <h3 className="section-title" style={{ marginBottom: 0 }}>My Curricula</h3>
        <button
          className="btn-primary"
          style={{ width: 'auto', marginTop: 0, padding: '10px 20px' }}
          onClick={() => setView('new-curriculum')}
        >
          + Create New Curriculum
        </button>
      </div>

      {curricula.length === 0 ? (
        <div className="empty-state">
          <span>📝</span>
          <p>No curricula yet. Create your first one to start assigning tasks.</p>
        </div>
      ) : (
        <div className="curriculum-grid animate-in" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {curricula
            .slice()
            .sort((a, b) => (a.grade || 0) - (b.grade || 0))
            .map((curr, idx) => {
              const currTasks = tasks.filter((t) => t.curriculumId === curr.id);
              const totalAssigned = currTasks.reduce((acc, t) => acc + (t.assignedStudentIds?.length || 0), 0);
              const currSubs = submissions.filter((s) => currTasks.find((t) => t.id === s.taskId)).length;
              const perc = totalAssigned > 0 ? Math.round((currSubs / totalAssigned) * 100) : 0;

              return (
                <div key={curr.id} className="student-task-card curr-card" style={{ animationDelay: `${idx * 0.05}s` }}>
                  {/* Top row: grade badge + curriculum name/board */}
                  <div className="curr-card-top">
                    <div className="student-avatar curr-card-avatar" style={{ borderRadius: '12px', background: 'var(--primary-light)', color: 'var(--primary)', flexShrink: 0 }}>
                      {curr.grade}
                    </div>
                    <div className="stc-info curr-card-info">
                      <strong className="stc-name">{curr.name}</strong>
                      <span className="stc-email">{BOARD_LABELS[curr.sourceBoard] || 'Custom Curriculum'}</span>
                    </div>
                  </div>

                  {/* Bottom row: stats + action buttons + optional progress */}
                  <div className="curr-card-bottom">
                    <div className="stc-task-stats">
                      <span className="stc-stat">
                        <span className="stc-stat-num">{currTasks.length}</span>
                        <span className="stc-stat-label">Tasks</span>
                      </span>
                      <span className="stc-divider">·</span>
                      <span className="stc-stat">
                        <span className="stc-stat-num stc-stat-num--done">{currSubs}</span>
                        <span className="stc-stat-label">Submissions</span>
                      </span>
                    </div>

                    <div className="curr-card-actions">
                      <button
                        className="btn-outline btn-outline--sm"
                        onClick={() => setView({ manage: curr })}
                        title="Manage Tasks"
                      >
                        📘 Manage
                      </button>
                      <button
                        className="btn-outline btn-outline--sm"
                        onClick={() => setView({ edit: curr })}
                        title="Edit Details"
                      >
                        ✏️ Edit
                      </button>
                      <button
                        className="btn-deny btn-deny--sm"
                        style={{ padding: '6px 10px', fontSize: '14px' }}
                        onClick={() => handleDeleteCurriculum(curr.id)}
                        title="Delete"
                      >
                        🗑
                      </button>
                    </div>

                    {totalAssigned > 0 && (
                      <div className="curr-card-progress">
                        <div className="stc-progress-bar">
                          <div className="stc-progress-fill" style={{ width: `${perc}%` }} />
                        </div>
                        <span className="curr-card-progress-label">{perc}% Bloom</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
        </div>
      )}
    </div>
  );

  if (view === 'new-curriculum') return <CurriculumEditor onSave={() => setView('dashboard')} onCancel={() => setView('dashboard')} />;
  if (view?.edit) return <CurriculumEditor existing={view.edit} approvedStudents={approvedStudents} onSave={() => setView('dashboard')} onCancel={() => setView('dashboard')} />;
  if (view?.manage) return <TeacherCurriculumView curriculum={view.manage} approvedStudents={approvedStudents} allSubmissions={submissions} onBack={() => setView('dashboard')} />;

  if (loadingData) return <div className="loading-screen">Loading...</div>;

  return (
    <div className="dashboard-wrapper">
      <header className="dashboard-header">
        <div className="header-left">
          <div className="student-avatar" style={{ width: 44, height: 44, fontSize: '18px' }}>{firstName.charAt(0)}</div>
          <div className="header-info">
            <h2 className="header-title">Hello, {firstName}!</h2>
            <p className="header-subtitle">Teacher Dashboard</p>
          </div>
        </div>
        <button className="btn-outline btn-outline--sm" onClick={handleSignOut}>Sign Out</button>
      </header>

      <div className="stats-row">
        <div className={`stat-card ${activeTab === 'onboarding' ? 'stat-card--active' : ''}`} onClick={() => setActiveTab('onboarding')}>
          <div className="stat-num">{pendingRequests.length}</div>
          <div className="stat-label">Pending</div>
        </div>
        <div className={`stat-card ${activeTab === 'students' ? 'stat-card--active' : ''}`} onClick={() => setActiveTab('students')}>
          <div className="stat-num">{approvedStudents.length}</div>
          <div className="stat-label">Students</div>
        </div>
        <div className={`stat-card ${activeTab === 'curricula' ? 'stat-card--active' : ''}`} onClick={() => setActiveTab('curricula')}>
          <div className="stat-num">{curricula.length}</div>
          <div className="stat-label">Curricula</div>
        </div>
      </div>

      <div className="dash-tabs">
        <button className={`dash-tab ${activeTab === 'students' ? 'dash-tab--active' : ''}`} onClick={() => setActiveTab('students')}>Students</button>
        <button className={`dash-tab ${activeTab === 'onboarding' ? 'dash-tab--active' : ''}`} onClick={() => setActiveTab('onboarding')}>Onboarding</button>
        <button className={`dash-tab ${activeTab === 'curricula' ? 'dash-tab--active' : ''}`} onClick={() => setActiveTab('curricula')}>Curricula</button>
      </div>

      <div className="tab-container">
        {activeTab === 'students' && renderStudentsTab()}
        {activeTab === 'onboarding' && renderOnboardingTab()}
        {activeTab === 'curricula' && renderCurriculaTab()}
      </div>
    </div>
  );
}
